import { INTRANET_CHAT_FORWARD_MAX, type IntranetChatConversation } from '@gb-ticket/shared-types';
import { useState } from 'react';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';

export function ChatForwardDialog({
  open,
  conversations,
  currentId,
  pending,
  onClose,
  onSubmit,
}: {
  open: boolean;
  conversations: IntranetChatConversation[];
  currentId: string;
  pending: boolean;
  onClose: () => void;
  onSubmit: (input: { targetConversationIds: string[]; comment: string }) => void;
}) {
  const choices = conversations.filter((item) => item.id !== currentId);
  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent>
        {open ? <ChatForwardForm choices={choices} pending={pending} onSubmit={onSubmit} /> : null}
      </DialogContent>
    </Dialog>
  );
}

function ChatForwardForm({
  choices,
  pending,
  onSubmit,
}: {
  choices: IntranetChatConversation[];
  pending: boolean;
  onSubmit: (input: { targetConversationIds: string[]; comment: string }) => void;
}) {
  const [selected, setSelected] = useState<string[]>([]);
  const [comment, setComment] = useState('');

  function toggle(id: string, checked: boolean) {
    setSelected((current) => {
      if (!checked) {
        return current.filter((item) => item !== id);
      }
      if (current.includes(id) || current.length >= INTRANET_CHAT_FORWARD_MAX) {
        return current;
      }
      return [...current, id];
    });
  }

  return (
    <>
      <DialogHeader>
        <DialogTitle>Weiterleiten</DialogTitle>
      </DialogHeader>
      {choices.length === 0 ? (
        <p className="text-sm text-muted-foreground">Es gibt kein weiteres Gespräch.</p>
      ) : (
        <ul className="max-h-64 space-y-2 overflow-y-auto">
          {choices.map((item) => {
            const checked = selected.includes(item.id);
            return (
              <li key={item.id} className="flex items-center gap-2">
                <Checkbox
                  checked={checked}
                  disabled={!checked && selected.length >= INTRANET_CHAT_FORWARD_MAX}
                  onCheckedChange={(value) => toggle(item.id, value === true)}
                  aria-label={item.title}
                />
                <span className="truncate text-sm">{item.title}</span>
              </li>
            );
          })}
        </ul>
      )}
      <Textarea
        value={comment}
        onChange={(event) => setComment(event.target.value)}
        aria-label="Eigener Text"
        placeholder="Eigener Text"
        rows={2}
      />
      <DialogFooter>
        <Button
          type="button"
          disabled={selected.length === 0 || pending}
          onClick={() => onSubmit({ targetConversationIds: selected, comment: comment.trim() })}
        >
          Senden
        </Button>
      </DialogFooter>
    </>
  );
}
